import { toI18nString } from '@/modules/core/date/date';
import useI18n, { I18n } from '@/modules/core/i18n/hooks/useI18n';
import { filterRecordsByActiveCriteriaInContextFilter } from '@/modules/solutions/solutionsFilters';
import { css } from '@emotion/react';
import Icon from '@/common/components/dataDisplay/Icon';
import size from 'lodash.size';
import { Clock, TriangleAlert } from 'lucide-react';
import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ContextFiltersSettings } from '@/modules/core/data/types/ContextFiltersSettings';
import { Asset } from '@/modules/core/data/types/Asset';
import { Campus } from '@/modules/core/data/types/Campus';
import { Contact } from '@/modules/core/data/types/Contact';
import { EducationalProgramStep } from '@/modules/core/data/types/EducationalProgramStep';
import { LabeledLink } from '@/modules/core/data/types/LabeledLink';
import { StudentSolution } from '@/modules/core/data/types/StudentSolution';
import { StudentSolutionsCategory } from '@/modules/core/data/types/StudentSolutionsCategory';
import { StudentSolutionContext } from '@/modules/core/data/types/StudentSolutionContext';
import CircleBtn from '@/components/dataDisplay/CircleBtn';
import Markdown from '@/components/dataDisplay/Markdown';
import Tooltip from '@/components/dataDisplay/Tooltip';
import ContactsSlider from './ContactsSlider';
import SolutionDocuments from './SolutionDocuments';
import SolutionLinks from './SolutionLinks';
import SolutionStamps from './SolutionStamps';

type Props = {
  documents: Asset[];
  links: LabeledLink[];
  benefitsDescription: string;
  campuses: Campus[];
  contacts: Contact[];
  context: StudentSolutionContext;
  contextFiltersSettings: ContextFiltersSettings;
  eligibilityConditionsDescription: string;
  description: string;
  educationalProgramSteps: EducationalProgramStep[];
  expiresAt: string;
  loanInterestRate: number;
  hasAnyContactToDisplay: boolean;
  loanMaxAmount: number;
  studentSolution: StudentSolution;
  categories: StudentSolutionsCategory[];

  // Report dialog
  isReportDialogOpen: boolean;
  setIsReportDialogOpen: (isOpen: boolean) => void;
  setSelectedStudentSolutionToReport: (studentSolution: StudentSolution) => void;
}

/**
 * The second row of the solution card is displayed when the user clicks on it.
 * It shows some additional information, and the layout depends on the device size.
 *
 * @param {Props} props
 * @return {JSX.Element}
 */
const SolutionCardDetails: React.FunctionComponent<Props> = (props): JSX.Element => {
  const {
    documents,
    links,
    benefitsDescription,
    campuses,
    contacts,
    context,
    contextFiltersSettings,
    eligibilityConditionsDescription,
    description,
    educationalProgramSteps,
    expiresAt,
    hasAnyContactToDisplay,
    loanInterestRate,
    loanMaxAmount,
    studentSolution,
    categories,
    isReportDialogOpen,
    setIsReportDialogOpen,
    setSelectedStudentSolutionToReport,
  }: Props = props;
  const { t } = useTranslation();
  const { lang }: I18n = useI18n();

  return (
    <div
      css={css`
        background-color: #E4E4E4;
        position: relative;
        margin-bottom: 60px;
        padding: 0 45px 0 45px;

        h3 {
          font-weight: bold;
          font-size: 17px;
          line-height: 1.5px;
          margin-bottom: 20px;
          display: contents;
        }

        .details-container {
          display: inline-flex;
          flex-direction: row;
          justify-content: space-between;
          width: 100%;

          .details {
            margin: 50px 20px 20px 20px;
            display: inline-flex;
            flex-direction: column;
            width: 40%;

            p, .markdown-container {
              font-weight: 400;
              font-size: 14px;
              line-height: 1.6;
              margin: 0;
            }

            .details-section {
              margin-bottom: 20px;

              &:last-child {
                margin-bottom: 0;
              }
            }
          }
        }

        .report-solution-button {
          position: absolute;
          bottom: 10px;
          right: 5%;
        }

        @media (max-width: 991.98px) {
          padding: 0 20px 0 20px;

          .details-container {
            flex-direction: column;

            .details {
              width: 90%;
              justify-content: center;
            }
          }

          .report-solution-button {
            bottom: 10px;
            right: 10%;
          }
        }
      `}
    >
      <div className={'details-container'}>
        <div className={'details'}>
          {
            description && (
              <div className={'details-section'}>
                <h3>{t('solutionsPage.solutionCard.description.label', 'Description')}</h3>
                <Markdown text={description} />
              </div>
            )
          }
          {
            loanInterestRate ? (
              <div className={'details-section'}>
                <p>
                  {t('solutionsPage.solutionCard.loanInterestRate.label', 'Taux d\'intérêt :')}
                  {' '}
                  {loanInterestRate}%
                </p>
              </div>
            ) : null
          }
          {
            loanMaxAmount ? (
              <div className={'details-section'}>
                <p>
                  {t('solutionsPage.solutionCard.loanMaxAmount.label', 'Montant max :')}
                  {' '}
                  {loanMaxAmount}€
                </p>
              </div>
            ) : null
          }
          {
            expiresAt ? (
              <div className={'details-section'}>
                <p>
                  <Icon icon={Clock} variant="light" />
                  {t('solutionsPage.solutionCard.expiresAt.label', 'Date d\'expiration :')}
                  {' '}
                  {toI18nString(expiresAt, lang)}
                </p>
              </div>
            ) : null
          }
          {
            !!size(documents) ? (
              <div className={'details-section'}>
                <SolutionDocuments documents={documents} />
              </div>
            ) : null
          }
          {
            !!size(links) ? (
              <div className={'details-section'}>
                <SolutionLinks links={links} />
              </div>
            ) : null
          }
          {
            hasAnyContactToDisplay && (
              <div className={'details-section'}>
                <ContactsSlider
                  contacts={contacts}
                  contactsMode={'full'}
                />
              </div>
            )
          }
        </div>
        <div className={'details'}>
          {
            (eligibilityConditionsDescription || benefitsDescription) && (
              <Fragment>
                {
                  eligibilityConditionsDescription && (
                    <div className={'details-section'}>
                      <h3>{t('solutionsPage.solutionCard.eligibilityConditionsDescription.label', 'Critères d\'obtention')}</h3>
                      <Markdown text={eligibilityConditionsDescription} />
                    </div>
                  )
                }
                {
                  benefitsDescription && (
                    <div className={'details-section'}>
                      <h3>{t('solutionsPage.solutionCard.benefits.label', 'Avantages')}</h3>
                      <Markdown text={benefitsDescription} />
                    </div>
                  )
                }
              </Fragment>
            )
          }
          {
            size(categories) ? (
              <div
                className={''}
                css={css`
                  display: none;

                  @media (max-width: 991.98px) {
                    display: block;
                    margin-bottom: 70px;
                  }
                `}
              >
                <SolutionStamps
                  contextFiltersSettings={contextFiltersSettings}
                  educationalProgramSteps={educationalProgramSteps}
                  campuses={campuses}
                  categories={filterRecordsByActiveCriteriaInContextFilter(context, studentSolution.categories, 'Category')}
                />
              </div>
            ) : null
          }
        </div>
      </div>
      <div className={'report-solution-button'}>
        {
          // Don't display the report dialog if the setIsReportDialogOpen isn't a function
          typeof setIsReportDialogOpen === 'function' && (
            <Tooltip overlay={<span>{t('solutionsPage.solutionCard.reportBtn.label', 'Signaler un problème')}</span>}>
              <CircleBtn
                onClick={() => {
                  // If dialog is already open, close it.
                  // Also, open dialog and set solution in proper order to avoid "blink" visual glitch (if the popup shows before the solution to show is set)
                  if (!isReportDialogOpen) {
                    setSelectedStudentSolutionToReport(studentSolution);
                    setIsReportDialogOpen(true);
                  } else {
                    setIsReportDialogOpen(false);
                    setSelectedStudentSolutionToReport(null);
                  }
                }}
              >
                <Icon icon={TriangleAlert} />
              </CircleBtn>
            </Tooltip>
          )
        }
      </div>
    </div>
  );
};

export default SolutionCardDetails;
